import * as Sentry from "@sentry/react";
import { Link, useRouter } from "@tanstack/react-router";
import { Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusPanel } from "@/components/ui/status-panel";

/**
 * Professional error fallback for unhandled errors caught by Sentry.ErrorBoundary.
 *
 * Used in:
 * - `AuthErrorBoundary` (wraps the entire authenticated app)
 * - `ProjectContent` error boundary (Phase 7)
 *
 * Shows a Sentry event ID when available so users can reference it in support requests.
 */
export function GenericErrorFallback({ resetError }: { resetError?: () => void }) {
  const router = useRouter();

  const eventId = Sentry.lastEventId();

  return (
    <StatusPanel
      title="Something went wrong"
      description="An unexpected error occurred. Please try refreshing the page."
      tone="destructive"
      size="page"
      className="flex-1"
      actions={
        <>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              resetError?.();
              router.invalidate();
            }}
          >
            <RefreshCw data-icon="inline-start" />
            Try Again
          </Button>
          <Link to="/">
            <Button size="lg">
              <Home data-icon="inline-start" />
              Home
            </Button>
          </Link>
        </>
      }
    >
      {eventId && <p className="text-xs text-muted-foreground/60">Error ID: {eventId}</p>}
    </StatusPanel>
  );
}
